import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '../state/session-store.js';
import {
  arcControl,
  MAP_HEIGHT,
  MAP_WIDTH,
  planeAt,
  TRAVEL_MS,
  travelProgress,
  travelRoute,
} from '../state/travel-map.js';

const ARC_STEPS = 48;

/**
 * Indiana Jones style travel montage: after the lift drops the player in the other office, a map
 * of Sweden draws a dashed line between the two cities with a plane flying along it. The Skip
 * button (bottom right) ends it at once; the welcome splash follows either way.
 */
export function TravelMap() {
  const travel = useSessionStore((s) => s.travel);
  const endTravel = useSessionStore((s) => s.endTravel);
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!travel) return;
    setProgress(0);
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const elapsed = now - start;
      setProgress(travelProgress(elapsed));
      if (elapsed >= TRAVEL_MS) endTravel();
      else frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [travel, endTravel]);

  const route = travel ? travelRoute(travel.from, travel.to) : undefined;
  if (!travel || !route) return null;

  const control = arcControl(route.from, route.to);
  const path = [`M ${route.from.x} ${route.from.y}`];
  for (let i = 1; i <= Math.ceil(ARC_STEPS * progress); i++) {
    const p = planeAt(route.from, route.to, Math.min(progress, i / ARC_STEPS));
    path.push(`L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`);
  }
  const plane = planeAt(route.from, route.to, progress);

  // Frame the trip with some margin instead of showing the whole country.
  const xs = [route.from.x, route.to.x, control.x];
  const ys = [route.from.y, route.to.y, control.y];
  const margin = 220;
  const minX = Math.max(0, Math.min(...xs) - margin);
  const maxX = Math.min(MAP_WIDTH, Math.max(...xs) + margin);
  const minY = Math.max(0, Math.min(...ys) - margin);
  const maxY = Math.min(MAP_HEIGHT, Math.max(...ys) + margin);

  return (
    <div className="travel-map" role="status" aria-label={t('travel.label', travel)}>
      <svg
        className="travel-map-svg"
        viewBox={`${minX} ${minY} ${maxX - minX} ${maxY - minY}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <image href="/travel/sweden-map.png" width={MAP_WIDTH} height={MAP_HEIGHT} />
        <path className="travel-map-casing" d={path.join(' ')} />
        <path className="travel-map-line" d={path.join(' ')} />
        <circle className="travel-map-city" cx={route.from.x} cy={route.from.y} r={9} />
        <circle className="travel-map-city" cx={route.to.x} cy={route.to.y} r={9} />
        <g transform={`translate(${plane.x} ${plane.y}) rotate(${plane.angle})`}>
          <path
            className="travel-map-plane"
            d="M 34 0 L 6 -9 L -6 -34 L -16 -34 L -8 -8 L -26 -8 L -32 -20 L -40 -20 L -36 0 L -40 20 L -32 20 L -26 8 L -8 8 L -16 34 L -6 34 L 6 9 Z"
          />
        </g>
      </svg>
      <button type="button" className="travel-map-skip" onClick={endTravel}>
        {t('travel.skip')}
      </button>
    </div>
  );
}
